import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";

// ── Right-panel step icons (inline SVG so no extra deps needed) ──────────────
const IconAssess = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="22" stroke="#22d3ee" strokeWidth="1.5" strokeDasharray="4 3" />
    <ellipse cx="24" cy="22" rx="10" ry="10" stroke="#22d3ee" strokeWidth="1.8" fill="none" />
    <circle cx="24" cy="22" r="4" fill="#22d3ee" fillOpacity="0.25" stroke="#22d3ee" strokeWidth="1.5" />
    <line x1="31" y1="29" x2="37" y2="35" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" />
    <circle cx="37" cy="35" r="2" fill="#22d3ee" />
    {/* tiny data bars */}
    <rect x="18" y="30" width="3" height="5" rx="1" fill="#22d3ee" fillOpacity="0.5" />
    <rect x="23" y="27" width="3" height="8" rx="1" fill="#22d3ee" fillOpacity="0.7" />
    <rect x="28" y="29" width="3" height="6" rx="1" fill="#22d3ee" fillOpacity="0.5" />
  </svg>
);

const IconAgents = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    {/* robot head */}
    <rect x="14" y="16" width="20" height="16" rx="4" stroke="#22d3ee" strokeWidth="1.8" fill="none" />
    <circle cx="20" cy="24" r="2.5" fill="#22d3ee" fillOpacity="0.3" stroke="#22d3ee" strokeWidth="1.5" />
    <circle cx="28" cy="24" r="2.5" fill="#22d3ee" fillOpacity="0.3" stroke="#22d3ee" strokeWidth="1.5" />
    {/* antenna */}
    <line x1="24" y1="16" x2="24" y2="10" stroke="#22d3ee" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="24" cy="9" r="2" fill="#22d3ee" />
    {/* arms */}
    <line x1="14" y1="24" x2="9" y2="27" stroke="#22d3ee" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="34" y1="24" x2="39" y2="27" stroke="#22d3ee" strokeWidth="1.8" strokeLinecap="round" />
    {/* gear overlay */}
    <circle cx="38" cy="12" r="5" stroke="#22d3ee" strokeWidth="1.2" fill="none" strokeDasharray="3 2" />
    <circle cx="38" cy="12" r="2" fill="#22d3ee" fillOpacity="0.4" />
  </svg>
);

const IconScale = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    {/* chart bars */}
    <rect x="8" y="28" width="7" height="12" rx="2" fill="#22d3ee" fillOpacity="0.4" stroke="#22d3ee" strokeWidth="1.5" />
    <rect x="18" y="20" width="7" height="20" rx="2" fill="#22d3ee" fillOpacity="0.55" stroke="#22d3ee" strokeWidth="1.5" />
    <rect x="28" y="13" width="7" height="27" rx="2" fill="#22d3ee" fillOpacity="0.7" stroke="#22d3ee" strokeWidth="1.5" />
    {/* trend arrow */}
    <polyline points="8,30 18,22 28,15 38,8" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <polyline points="33,7 38,8 37,13" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

// ── Step data ─────────────────────────────────────────────────────────────────
const steps = [
  {
    Icon: IconAssess,
    title: "1. ASSESS YOUR DATA:",
    desc: "Connect your existing sources for a free data maturity report.",
  },
  {
    Icon: IconAgents,
    title: "2. CONFIGURE AGENTS:",
    desc: "Select and customize pre-built Intelligent Business Agents for your use case.",
  },
  {
    Icon: IconScale,
    title: "3. SCALE & OPTIMIZE:",
    desc: "Deploy and monitor your solutions with our Axtream platform.",
  },
];

// ── Single mobile accordion dropdown (Home / AI Innovation Hub / Case Studies) ─
const MobileDropdown = ({
  itemKey,
  dropdownItems,
  dropdownLabels,
  activeMobile,
  setActiveMobile,
  goToItem,
  setMobileOpen,
}) => (
  <div>
    <div
      className="flex justify-between items-center cursor-pointer"
      onClick={() => setActiveMobile(activeMobile === itemKey ? null : itemKey)}
    >
      <span>{dropdownLabels[itemKey]}</span>
      <ChevronDown
        size={18}
        className={`transition-transform duration-300 ${
          activeMobile === itemKey ? "rotate-180" : ""
        }`}
      />
    </div>

    <AnimatePresence>
      {activeMobile === itemKey && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="overflow-hidden"
        >
          <div className="ml-4 mt-3 space-y-3 text-sm text-gray-300 border-l border-white/10 pl-4">
            {dropdownItems[itemKey].columns.flat().map((item, i) => (
              <div
                key={i}
                className="cursor-pointer hover:text-cyan-400 transition-colors duration-200"
                onClick={() => {
                  goToItem(item.path);
                  setMobileOpen(false);
                  setActiveMobile(null);
                }}
              >
                {item.name}
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const NavbarDemo = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMobile, setActiveMobile] = useState(null);
  const [hovered, setHovered] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [showNavbar, setShowNavbar] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const lastScrollY = useRef(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 50);
      if (Math.abs(currentScrollY - lastScrollY.current) < 10) return;
      setShowNavbar(true);
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => { document.body.style.overflow = mobileOpen ? "hidden" : "auto"; }, [mobileOpen]);

  const navItem =  `
    relative flex items-center gap-1 cursor-pointer
    text-sm md:text-base font-medium transition-colors duration-300
    ${scrolled ? "text-gray-800 hover:text-cyan-500" : "text-white hover:text-cyan-300"}
  `;

  // "home" holds the Company / Team dropdown that replaces the old plain "Company" link.
  // Order below drives both desktop mega-menu order and mobile accordion order.
  const dropdownItems = {
    home: {
      title: "COMPANY",
    
      columns: [
        [
          { name: "About US", path: "/about" },
          { name: " Leadership Team", path: "/team" },
        ],
      ],
    },
    ai: {
      title: "AI INNOVATION HUB",
      path: "/product/axtream",
      columns: [
        [
          { name: "Axtream", path: "/product/axtream", logo: "/assets/axtream_logo_sm.png", },
          { name: "Infinisight", path: "/product/infinisight",logo: "/assets/infinisight_banner_logo.png", },
          { name: "Intelligent Business Agents", path: "/product/Intelligent", logo: "/assets/deliveryailogo.png", },
        ],
      ],
    },
    case: {
      title: "CASE STUDIES",
      path: "/casestudies",
      columns: [[{ name: "Success Stories", path: "/casestudies" }], [{ name: "Client Projects" }]],
    },
  };

  const dropdownLabels = {
    home: "Home",
    ai: "AI Innovation Hub",
    case: "Case Studies",
  };

  const handleEnter = (key) => { clearTimeout(timeoutRef.current); setHovered(key); };
  const handleLeave = () => { timeoutRef.current = setTimeout(() => setHovered(null), 150); };

  // Navigates to a route, then scrolls to an in-page anchor once the target page has rendered.
  const goToItem = (path) => {
    if (!path) return;
    if (path.startsWith("http")) {
      window.open(path, "_blank");
      return;
    }
    const [route, hash] = path.split("#");
    navigate(route || "/");
    if (hash) {
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  if (location.pathname === "/demo") return null;

  return (
    <motion.div
      animate={{ y: showNavbar ? 0 : -120 }}
      transition={{ duration: 0.3 }}
      className={`fixed left-0 w-full z-50 transition-all duration-300 ${
        mobileOpen
    ? "bg-white shadow-md lg:bg-transparent"
        :scrolled
          ? "bg-white/90 backdrop-blur-md backdrop-saturate-150 shadow-md"
          : "bg-transparent top-2 lg:top-4"
      }`}
    >
      {/* ── NAVBAR BAR ── */}
      <div className="max-w-[1400px] mx-auto min-h-[70px] lg:min-h-[90px] flex items-center justify-between px-4">
        <Link to="/">
          <img
            src={scrolled ? "/assets/Logo_header.png" : "/assets/Logo_header_white.png"}
            alt="logo"
            className="h-9 md:h-10 w-auto object-contain"
          />
        </Link>

        {/* DESKTOP NAV */}
        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          {/* "Home" sits where the old plain "Company" link used to be */}
          <div
            className={navItem}
            onMouseEnter={() => handleEnter("home")}
            onMouseLeave={handleLeave}
            onClick={() => { navigate(dropdownItems.home.path); setHovered(null); }}
          >
            {dropdownLabels.home}
            <ChevronDown size={16} />
          </div>

          <Link to="/solution"><div className={navItem}>Solutions</div></Link>

          {["ai", "case"].map((key) => (
            <div
              key={key}
              className={navItem}
              onMouseEnter={() => handleEnter(key)}
              onMouseLeave={handleLeave}
              onClick={() => { navigate(dropdownItems[key].path); setHovered(null); }}
            >
              {dropdownLabels[key]}
              <ChevronDown size={16} />
            </div>
          ))}

          <div className={navItem} onClick={() => navigate("/industries")}>Industries</div>
          <div className={navItem} onClick={() => navigate("/blog")}>Insights</div>

          <button
            onClick={() => navigate("/demo")}
            className="bg-[#FF3366] text-white px-4 md:px-5 py-2 rounded-lg text-sm md:text-base hover:scale-105 transition"
          >
            Request Demo
          </button>
        </div>

        {/* MOBILE ICON */}
        <div className="lg:hidden">
          {mobileOpen ? (
            <X size={28} className={scrolled ? "text-white" : "text-white"} onClick={() => setMobileOpen(false)} />
          ) : (
            <Menu size={28} className={scrolled ? "text-black" : "text-white"} onClick={() => setMobileOpen(true)} />
          )}
        </div>
      </div>

      {/* ── MEGA MENU ── */}
      <AnimatePresence>
        {hovered && (
          <motion.div
            key={hovered}
            onMouseEnter={() => handleEnter(hovered)}
            onMouseLeave={handleLeave}
            initial={{ opacity: 0, scale: 0.97, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ duration: 0.22 }}
            className="absolute left-0 top-full w-full px-4 sm:px-6 lg:px-8"
          > 
            <div className="max-w-5xl mx-auto bg-[#1e2233] text-white rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
            
              <div className="flex flex-col md:flex-row">
                

                {/* ── LEFT: nav links ── */}
                <div className="flex-1 p-8 md:p-6">
                  <p className="text-gray-400 text-[10px] mb-4 tracking-[0.18em] uppercase">
                    {dropdownItems[hovered].title}
                  </p>
                  <div className="space-y-5">
                    {dropdownItems[hovered].columns.flat().map((item, i) => (
                      <div
  key={i}
  onClick={() => {
    goToItem(item.path);
    setHovered(null);
  }}
  className="flex items-center gap-3 text-[17px] font-medium text-gray-100 hover:text-cyan-400 cursor-pointer transition-colors duration-200 group"
>
 {/* Show logo box only for AI products menu */}
  {hovered === "ai" && (
    <div className="w-20 h-16 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center p-1 group-hover:border-cyan-400/40 transition">
      {item.logo && (
        <img
          src={item.logo}
          alt={item.name}
          className="max-w-full max-h-full object-contain"
        />
      )}
    </div>
  )}

  <span>{item.name}</span>
</div>
                    ))}
                  </div>
                </div>

                {/* ── DIVIDER ── */}
                <div className="hidden md:block w-px bg-white/10 my-2" />

                {/* ── RIGHT: "Ready to get started?" panel ── */}
                <div className="w-full md:w-[520px] bg-[#242938] p-2 md:p-3 flex flex-col gap-3">
                  <p className="text-white font-bold text-lg tracking-widest text-center uppercase">
                    Ready to Get Started?
                  </p>

                  {/* 3-step grid */}
                  <div className="grid grid-cols-3 gap-4">
                    {steps.map(({ Icon, title, desc }, i) => (
                      <div key={i} className="flex flex-col items-center text-center gap-2">
                        {/* icon circle */}
                        <div className="w-12 h-12 rounded-full bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
                          <Icon />
                        </div>
                        <p className="text-[12px] font-bold text-white leading-snug">{title}</p>
                        <p className="text-[12px] text-gray-400 leading-relaxed">{desc}</p>
                      </div>
                    ))}
                  </div>

                  {/* discovery call link */}
                  <div className="text-center">
                    <button
    
                      onClick={(e) => { e.preventDefault(); navigate("/contact"); setHovered(null); }}
                      className="text-cyan-400 text-sm underline underline-offset-2 hover:text-cyan-300 transition-colors"
                    >
                      Book a discovery call with an AI expert.
                    </button>
                  </div>

                  {/* CTA */}
                  <div className="flex justify-center">
                    <button
                      onClick={() => { navigate("/demo"); setHovered(null); }}
                      className="bg-gradient-to-r from-cyan-400 to-blue-500 text-white px-8 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity shadow-lg shadow-cyan-500/20"
                    >
                      Get Started
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── MOBILE MENU ── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
         
          
            <motion.div
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              className="fixed right-0 top-0 h-full w-[80%] sm:w-[70%] max-w-sm bg-[#242938] z-50 p-6 "
            >
               <div className="flex justify-end mb-6">
    <button onClick={() => setMobileOpen(false)}>
      <X size={28} className="text-white" />
    </button>
  </div>
              <div className="space-y-6 text-white text-base">
                {/* "Home" accordion sits where the old plain "Company" link used to be */}
                <MobileDropdown
                  itemKey="home"
                  dropdownItems={dropdownItems}
                  dropdownLabels={dropdownLabels}
                  activeMobile={activeMobile}
                  setActiveMobile={setActiveMobile}
                  goToItem={goToItem}
                  setMobileOpen={setMobileOpen}
                />

                <div onClick={() => navigate("/solution")}>Solutions</div>

                {["ai", "case"].map((key) => (
                  <MobileDropdown
                    key={key}
                    itemKey={key}
                    dropdownItems={dropdownItems}
                    dropdownLabels={dropdownLabels}
                    activeMobile={activeMobile}
                    setActiveMobile={setActiveMobile}
                    goToItem={goToItem}
                    setMobileOpen={setMobileOpen}
                  />
                ))}

                <div onClick={() => navigate("/industries")}>Industries</div>
                <div onClick={() => navigate("/blog")}>Insights</div>

                {/* Mobile "Get Started" steps summary */}
                <div className="border border-white/10 rounded-xl p-4 space-y-3 bg-white/5">
                  <p className="text-xs font-bold tracking-widest text-center uppercase text-white">
                    Ready to Get Started?
                  </p>
                  {steps.map(({ title, desc }, i) => (
                    <div key={i} className="text-xs text-gray-300">
                      <span className="font-semibold text-white">{title}</span> {desc}
                    </div>
                  ))}
                  <button
                    className="block text-center text-cyan-400 text-xs underline mt-1"
                    onClick={(e) => { e.preventDefault(); navigate("/contact"); setMobileOpen(false); }}
                  >
                    Book a discovery call with an AI expert.
                  </button>
                </div>

                <button
                  onClick={() => navigate("/demo")}
                  className="w-full bg-[#FF3366] py-3 rounded-full text-sm"
                >
                  Request Demo
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default NavbarDemo;